// src/context/GoogleMapsContext.js
// Centralized Google Maps loader — load the SDK ONCE for the entire app.
// All pages/components consume this via useGoogleMaps() instead of calling useLoadScript individually.
import React, { createContext, useContext } from 'react';
import { useLoadScript } from '@react-google-maps/api';

// IMPORTANT: This array must be defined OUTSIDE the component so it has a stable reference.
// If defined inside a component, a new array is created every render and @react-google-maps/api
// throws a "Changing libraries is not supported" warning and can reload the SDK.
const LIBRARIES = ['places', 'maps', 'marker'];

const GoogleMapsContext = createContext({ isLoaded: false, loadError: null, isMapsAvailable: false });

const GoogleMapsProviderWithKey = ({ children }) => {
const { isLoaded, loadError } = useLoadScript({
googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
libraries: LIBRARIES,
});

const isMapsAvailable = isLoaded && !loadError && typeof window !== 'undefined' && !!(window.google && window.google.maps && window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement);

return (
<GoogleMapsContext.Provider value={{ isLoaded, loadError, isMapsAvailable }}>
{children}
</GoogleMapsContext.Provider>
);
};

const GoogleMapsProviderWithoutKey = ({ children }) => (
<GoogleMapsContext.Provider value={{ isLoaded: false, loadError: null, isMapsAvailable: false }}>
{children}
</GoogleMapsContext.Provider>
);

export const GoogleMapsProvider = ({ children }) => {
const hasApiKey = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

if (hasApiKey) {
return <GoogleMapsProviderWithKey>{children}</GoogleMapsProviderWithKey>;
}

return <GoogleMapsProviderWithoutKey>{children}</GoogleMapsProviderWithoutKey>;
};

export const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || null;

export const useGoogleMaps = () => useContext(GoogleMapsContext);
